import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/Shared';
import './Bookings.css';

export default function DoctorAppointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user || user.role !== 'doctor') {
      navigate('/login?role=doctor');
      return;
    }
    axios.get('/api/appointments/doctor')
      .then(r => { setAppointments(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user, navigate]);

  const updateStatus = async (id, status, reason = '') => {
    try {
      await axios.patch(`/api/appointments/${id}/status`, { status, cancelReason: reason });
      setAppointments(appointments.map(a => a._id === id ? { ...a, status } : a));
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating status');
    }
  };

  const addPrescription = async (id) => {
    const prescription = prompt('Enter prescription notes:');
    if (prescription) {
      try {
        await axios.patch(`/api/appointments/${id}/prescription`, { prescription });
        setAppointments(appointments.map(a => a._id === id ? { ...a, prescription } : a));
        alert('Prescription saved!');
      } catch (err) {
        alert('Error saving prescription');
      }
    }
  };

  const filtered = filter === 'all' ? appointments
    : appointments.filter(a => a.status === filter);

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
  };

  return (
    <div className="bookings-page">
      <div className="bk-header">
        <div className="container">
          <h1>📋 My Appointments</h1>
          <p>Manage your patient consultations</p>
        </div>
      </div>

      <div className="container bk-body">
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <div style={{ padding: '16px', background: 'var(--teal-l)', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--teal)' }}>{stats.total}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Total Appointments</div>
          </div>
          <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#d97706' }}>{stats.pending}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Pending</div>
          </div>
          <div style={{ padding: '16px', background: '#e0e7ff', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#4f46e5' }}>{stats.confirmed}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Confirmed</div>
          </div>
          <div style={{ padding: '16px', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669' }}>{stats.completed}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Completed</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {['all', 'pending', 'confirmed', 'completed'].map(s => (
            <button
              key={s}
              className={`btn btn-sm ${filter === s ? 'btn-teal' : 'btn-outline'}`}
              onClick={() => setFilter(s)}
              style={{ textTransform: 'capitalize' }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="page-loader">Loading appointments…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No {filter !== 'all' ? filter : ''} appointments</h3>
            <p>Your consultations will appear here</p>
          </div>
        ) : (
          <div>
            {filtered.map(apt => (
              <div key={apt._id} className="booking-card" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
                    <div className="br-avatar rose-av" style={{ minWidth: '48px', height: '48px' }}>
                      {apt.patient?.firstName?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong>{apt.patient?.firstName} {apt.patient?.lastName}</strong>
                      <div style={{ color: 'var(--muted)', fontSize: '13px', marginTop: '4px' }}>
                        📅 {new Date(apt.bookingDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {apt.timeSlot}
                      </div>
                      <div style={{ color: 'var(--muted)', fontSize: '13px' }}>
                        📞 {apt.patient?.phone}
                      </div>
                      {apt.patient?.email && (
                        <div style={{ color: 'var(--muted)', fontSize: '13px' }}>
                          📧 {apt.patient?.email}
                        </div>
                      )}
                      {apt.notes && (
                        <div style={{ color: 'var(--muted)', fontSize: '13px', marginTop: '4px', fontStyle: 'italic' }}>
                          "{apt.notes}"
                        </div>
                      )}
                      {apt.prescription && (
                        <div style={{ color: 'var(--teal)', fontSize: '13px', marginTop: '4px', background: '#e0f7f4', padding: '6px 8px', borderRadius: '4px' }}>
                          💊 Prescription: {apt.prescription}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: '120px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--teal)', marginBottom: '8px' }}>
                      ₨{apt.totalAmount?.toLocaleString()}
                    </div>
                    <StatusBadge status={apt.status} />
                  </div>
                </div>

                {/* Expanded details */}
                {expandedId === apt._id && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {apt.status === 'pending' && (
                      <>
                        <button
                          className="btn btn-teal btn-sm"
                          onClick={() => { updateStatus(apt._id, 'confirmed'); setExpandedId(null); }}
                        >
                          ✅ Accept
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: 'var(--rose-l)', color: 'var(--rose)', borderRadius: '50px', border: 'none' }}
                          onClick={() => { updateStatus(apt._id, 'cancelled', 'Declined by doctor'); setExpandedId(null); }}
                        >
                          ❌ Decline
                        </button>
                      </>
                    )}
                    {apt.status === 'confirmed' && (
                      <>
                        <button
                          className="btn btn-teal btn-sm"
                          onClick={() => { updateStatus(apt._id, 'completed'); setExpandedId(null); }}
                        >
                          ✅ Mark Complete
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => addPrescription(apt._id)}
                        >
                          💊 Add Prescription
                        </button>
                        <a href="https://meet.google.com" target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
                          📹 Start Video Call
                        </a>
                      </>
                    )}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setExpandedId(null)}
                    >
                      Close
                    </button>
                  </div>
                )}

                {/* Toggle button */}
                <button
                  style={{ marginTop: expandedId !== apt._id ? '8px' : '0', background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                  onClick={() => setExpandedId(expandedId === apt._id ? null : apt._id)}
                >
                  {expandedId === apt._id ? '▼ Less' : '▶ More'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
